<template>
  <el-dialog
    :visible="visible"
    :title="dialogTitle"
    width="1200px"
    append-to-body
    :close-on-click-modal="false"
    @open="handleOpen"
    @close="handleClose"
  >
    <div class="bill-send-records-dialog">
      <div class="bill-send-records-dialog__toolbar">
        <bean-dialog-form
          v-if="canCreate && customerId"
          :columns="createFormColumns"
          :value="createForm"
          :submitHandler="handleCreate"
          :reset-form-on-open-dialog="true"
          title="新建账单发送记录"
        >
          <el-button type="primary">新建账单发送记录</el-button>
        </bean-dialog-form>
      </div>

      <div class="bill-send-records-dialog__table" v-loading="loading">
        <bean-table
          :columns="tableColumns"
          :value="records"
          :actions="actions"
          :action-column-props="actionColumnProps"
        />
      </div>

      <div class="bill-send-records-dialog__pagination">
        <bean-pagination
          v-if="pagination.total > 0"
          v-bind="pagination"
          @submit="handlePagination"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { abilityService } from 'beans-admin-plugin';
import { buildBillSendRecordActions, buildBillSendRecordColumns, buildBillSendRecordFormColumns } from '@/views/bill_send_records/shared';

@Component
export default class BillSendRecordsDialog extends Vue {
  @Prop(Boolean) visible;
  @Prop({ type: Object, default: () => ({}) }) customer;

  loading = false;
  records = [];
  pagination = {};

  defaultParams = {
    page: 1,
    per_page: 10,
  };

  createForm = {};

  get canCreate() {
    return abilityService.can('bill_send_records.create');
  }

  get customerId() {
    return this.customer && this.customer.id;
  }

  get dialogTitle() {
    const customerName = this.customer && this.customer.name;
    return customerName ? `${customerName} - 账单发送记录` : '账单发送记录';
  }

  get tableColumns() {
    return buildBillSendRecordColumns({ type: 'index', includeCustomer: false });
  }

  get createFormColumns() {
    return buildBillSendRecordFormColumns({ data: this.createForm, type: 'new', includeCustomer: false });
  }

  get actionColumnProps() {
    return {
      minWidth: 260,
    };
  }

  actions(scope) {
    return buildBillSendRecordActions(this, scope, {
      remove: this.handleDelete,
      reload: this.reloadCurrentPage,
    }, {
      editAction: (row) => ({
        component: 'dialogFormButton',
        props: {
          label: this.$t('edit'),
          columns: buildBillSendRecordFormColumns({ data: row, type: 'edit', includeCustomer: false }),
          value: {
            send_method: row.send_method,
            sent_at: row.sent_at,
          },
          resetFormOnOpenDialog: true,
          submitHandler: async (value) => {
            await this.handleEdit(row, value);
          }
        }
      })
    });
  }

  async fetchData(params = {}) {
    if (!this.customerId) {
      this.records = [];
      this.pagination = {};
      return;
    }

    this.loading = true;
    try {
      const response = await this.$request.get('/bill_send_records', {
        params: {
          ...this.defaultParams,
          customer_id_eq: this.customerId,
          ...params,
        }
      });

      this.records = response.data;
      this.pagination = response.pagination;
    } finally {
      this.loading = false;
    }
  }

  async reloadCurrentPage() {
    await this.fetchData({
      page: this.pagination['current-page'] || this.defaultParams.page,
      per_page: this.pagination['page-size'] || this.defaultParams.per_page,
    });
    this.$emit('updated');
  }

  async handleCreate(value) {
    await this.$autoLoading(this.$request.post('/bill_send_records', { ...value, customer_id: this.customerId }));
    this.$message.success(this.$t('operateSuccess'));
    await this.fetchData({
      page: 1,
      per_page: this.pagination['page-size'] || this.defaultParams.per_page,
    });
    this.$emit('updated');
  }

  async handleEdit(row, value) {
    await this.$autoLoading(this.$request.put(`/bill_send_records/${row.id}`, {
      ...value,
      customer_id: this.customerId,
    }));
    this.$message.success(this.$t('operateSuccess'));
    await this.reloadCurrentPage();
  }

  async handleDelete(id) {
    await this.$autoLoading(this.$request.delete(`/bill_send_records/${id}`));
    this.$message.success(this.$t('operateSuccess'));

    const currentPage = this.pagination['current-page'] || this.defaultParams.page;
    const targetPage = this.records.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;

    await this.fetchData({
      page: targetPage,
      per_page: this.pagination['page-size'] || this.defaultParams.per_page,
    });
    this.$emit('updated');
  }

  handleOpen() {
    this.fetchData();
  }

  handleClose() {
    this.$emit('update:visible', false);
  }

  handlePagination({ page, size }) {
    this.fetchData({ page, per_page: size });
  }

  @Watch('customerId')
  onCustomerIdChange() {
    if (this.visible) {
      this.fetchData();
    }
  }
}
</script>

<style lang="scss" scoped>
.bill-send-records-dialog {
  min-height: 420px;
}

.bill-send-records-dialog__toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.bill-send-records-dialog__table {
  height: 480px;
}

.bill-send-records-dialog__pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
